import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import Button from '@/components/Button';
import ContentSpinner from '@/components/ContentSpinner';
import { PageHeading, TeamHeading } from '@/components/Layout/Parts';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { ReportsGroupResource } from '@/types/resources';
import { api } from '@/utils/api';
import { RootState } from '@/utils/store';

import ReportForm from './ReportForm';
import ReportsTable from './ReportsTable';

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 26px;
`;

const StyledPageHeading = styled(PageHeading)`
  margin: 0;
`;

const Navigation = styled.div`
  margin-bottom: 16px;
`;

const BackButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  ${Text} {
    margin-left: 12px;
  }
`;

const SingleGroup = () => {
  const [showFormDialog, setShowFormDialog] = useState(false);

  const { groupId, teamId } = useParams();

  const theme = useTheme();

  const userRole = useSelector(
    (state: RootState) => state.auth.user!.role.name,
  );

  const navigate = useNavigate();

  const alerts = useAlerts();

  const { isLoading, data: group } = useQuery(
    ['reports_groups', { id: groupId }],
    () => api.get<never, ReportsGroupResource>(`/v1/reports-groups/${groupId}`),
    {
      staleTime: 120 * 1000,
      retry: 2,
      onError: (err) => {
        if ((err as any).response.status === 404) {
          return navigate('/404', {
            replace: true,
          });
        }
        alerts.error();
      },
    },
  );

  if (isLoading || !group) {
    return <ContentSpinner />;
  }

  return (
    <>
      <TeamHeading />
      <HeadingRow>
        <StyledPageHeading>{group.name}</StyledPageHeading>
        <Button onClick={() => setShowFormDialog(true)}>Add Report</Button>
      </HeadingRow>
      <Navigation>
        <BackButton onClick={() => navigate(`/teams/${teamId}/reports/other`)}>
          <svg
            width="34"
            height="34"
            viewBox="0 0 34 34"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17 33C8.16344 33 1 25.8366 1 17C1 8.16344 8.16344 1 17 1C25.8366 1 33 8.16344 33 17C33 25.8366 25.8366 33 17 33Z"
              stroke={theme.colors.lightGray}
              strokeMiterlimit="10"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M15.4288 12.2821C15.8253 11.8976 16.4584 11.9074 16.8429 12.3038C17.2274 12.7003 17.2177 13.3334 16.8212 13.7179L14.468 16H23C23.5523 16 24 16.4477 24 17C24 17.5523 23.5523 18 23 18H14.468L16.8212 20.2821C17.2177 20.6666 17.2274 21.2997 16.8429 21.6962C16.4584 22.0927 15.8253 22.1024 15.4288 21.7179L11.3043 17.7179C11.1101 17.5295 11.0005 17.2705 11.0005 17C11.0005 16.7295 11.1101 16.4705 11.3043 16.2821L15.4288 12.2821Z"
              fill={theme.colors.black}
            />
          </svg>
          <Text $type="button">Back</Text>
        </BackButton>
      </Navigation>
      <ReportsTable
        groupId={group.id}
        userRole={userRole}
        isGroupRestricted={group.isRestricted}
      />
      <AnimatePresence>
        {showFormDialog && (
          <ReportForm
            onClose={() => setShowFormDialog(false)}
            groupId={group.id}
            isGroupRestricted={group.isRestricted}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default SingleGroup;
